import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import { t } from "../../lib/i18n";
import { ShoppingBag, User, LogOut, Menu, X, Package, Shield } from "lucide-react";
import { Button } from "../ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
  const { lang, setLang, currency, setCurrency, cart, user, logout, config } = useStore();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  React.useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const avatarSrc = user?.avatar_url
    ? user.avatar_url.startsWith("http") ? user.avatar_url : `${API_BASE}${user.avatar_url}`
    : null;

  const navLinks = [
    { to: "/", label: t(lang, "home") },
    { to: "/products", label: t(lang, "products") },
    { to: "/reviews", label: t(lang, "reviews") },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-[#e8ddc9]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4 md:gap-8">
        <Link to="/" data-testid="logo-link" className="flex flex-col items-start leading-none shrink-0">
          <span className="font-logo text-3xl md:text-4xl" style={{ color: "var(--thiqa-primary)" }}>الثقة</span>
          <span className="text-[10px] md:text-xs tracking-widest uppercase" style={{ color: "var(--thiqa-gold)" }}>AL-THIQA · İSTANBUL</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 flex-1">
          {navLinks.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace("/", "") || "home"}`}
              className={`text-sm font-medium hover:text-[color:var(--thiqa-primary)] transition-colors ${
                location.pathname === n.to ? "text-[color:var(--thiqa-primary)]" : "text-gray-700"
              }`}>{n.label}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 ms-auto">
          <select data-testid="lang-switcher" value={lang} onChange={(e) => setLang(e.target.value)}
            className="text-xs md:text-sm bg-transparent border border-gray-300 rounded-full px-2 py-1 md:px-3 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--thiqa-primary)]">
            <option value="ar">AR</option><option value="en">EN</option>
          </select>

          <select data-testid="currency-switcher" value={currency} onChange={(e) => setCurrency(e.target.value)}
            className="text-xs md:text-sm bg-transparent border border-gray-300 rounded-full px-2 py-1 md:px-3 md:py-1.5 focus:outline-none focus:ring-2 focus:ring-[color:var(--thiqa-primary)]">
            {config && Object.values(config.currencies).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>

          <Link to="/cart" data-testid="cart-link" className="relative p-2 hover:text-[color:var(--thiqa-primary)] transition-colors">
            <ShoppingBag size={22} />
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -end-1 bg-[color:var(--thiqa-primary)] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Link>

          {user ? (
            <div ref={menuRef} className="relative">
              <button data-testid="user-menu-toggle" onClick={() => setMenuOpen((v) => !v)}
                className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#e8ddc9] hover:border-[color:var(--thiqa-primary)] transition bg-[#f5efe4] flex items-center justify-center">
                {avatarSrc ? <img src={avatarSrc} alt="me" className="w-full h-full object-cover" /> : <User size={16} className="text-gray-500" />}
              </button>
              {menuOpen && (
                <div className="absolute end-0 mt-2 w-56 bg-white border border-[#e8ddc9] rounded-xl shadow-lg overflow-hidden" data-testid="user-menu">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="text-sm font-semibold truncate">{user.name}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <MenuItem to="/profile" icon={User} label={lang === "ar" ? "الملف الشخصي" : "My Profile"} onClick={() => setMenuOpen(false)} testid="menu-profile" />
                  <MenuItem to="/orders" icon={Package} label={t(lang, "orders")} onClick={() => setMenuOpen(false)} testid="menu-orders" />
                  {user.role === "admin" && (
                    <MenuItem to="/admin" icon={Shield} label={t(lang, "admin_dashboard")} onClick={() => setMenuOpen(false)} testid="menu-admin" />
                  )}
                  <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}
                    className="w-full text-start px-4 py-3 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid="menu-logout">
                    <LogOut size={16} />{t(lang, "logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="hidden md:block">
              <Button size="sm" className="btn-primary-thiqa rounded-full">{t(lang, "login")}</Button>
            </Link>
          )}

          <button data-testid="mobile-menu-toggle" className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-2">
          {navLinks.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="block py-2 text-sm">{n.label}</Link>
          ))}
          {user ? (
            <>
              <Link to="/profile" onClick={() => setMobileOpen(false)} className="block py-2 text-sm">{lang === "ar" ? "الملف الشخصي" : "My Profile"}</Link>
              <Link to="/orders" onClick={() => setMobileOpen(false)} className="block py-2 text-sm">{t(lang, "orders")}</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block py-2 text-sm">{t(lang, "admin_dashboard")}</Link>}
              <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="block py-2 text-sm">{t(lang, "logout")}</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobileOpen(false)} className="block py-2 text-sm">{t(lang, "login")}</Link>
          )}
        </div>
      )}
    </header>
  );
};

const MenuItem = ({ to, icon: Icon, label, onClick, testid }) => (
  <Link to={to} onClick={onClick} data-testid={testid} className="w-full flex items-center gap-2 px-4 py-3 text-sm hover:bg-[#faf3e3]">
    <Icon size={16} /> {label}
  </Link>
);
